import type { FC } from 'react';
import { Button, Flex, Skeleton, Typography } from 'antd';
import { ArrowDownOutlined, ClusterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import { useWorkOrderOptimizerTeaser } from './hooks/useWorkOrderOptimizerTeaser';

export interface WorkOrderOptimizerTeaserProps {
  organizationId: string;
  siteFilter: string;
  onOpenSuggestions: () => void;
}

export const WorkOrderOptimizerTeaser: FC<WorkOrderOptimizerTeaserProps> = ({
  organizationId,
  siteFilter,
  onOpenSuggestions,
}) => {
  const { t } = useTranslation('maintenance');
  const teaser = useWorkOrderOptimizerTeaser({ organizationId, siteFilter });

  if (teaser.isError) {
    return null;
  }

  const hasSuggestions = teaser.suggestionCount > 0;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
        background: hasSuggestions ? '#ecfdf5' : '#fff',
        border: `1px solid ${
          hasSuggestions ? '#a7f3d0' : WORK_ORDER_UI.cardBorder
        }`,
        borderRadius: 12,
        padding: '10px 14px',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <Flex align="center" gap={12} style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: hasSuggestions ? WORK_ORDER_UI.primary : '#e2e8f0',
            color: hasSuggestions ? '#fff' : '#64748b',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ClusterOutlined />
        </span>
        <div style={{ minWidth: 0 }}>
          <Flex align="center" gap={8} wrap="wrap">
            <Typography.Text
              strong
              style={{ color: WORK_ORDER_UI.sidebarNavy }}
            >
              {t('workOrders.kanban.optimizer.teaserTitle')}
            </Typography.Text>
            {!teaser.isLoading && hasSuggestions ? (
              <span
                style={{
                  minWidth: 22,
                  height: 22,
                  padding: '0 7px',
                  borderRadius: 999,
                  background: WORK_ORDER_UI.primary,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={t('workOrders.kanban.optimizer.teaserTotalHint', {
                  total: teaser.suggestionCount,
                })}
              >
                {teaser.suggestionCount}
              </span>
            ) : null}
          </Flex>
          {teaser.isLoading ? (
            <Skeleton
              active
              title={false}
              paragraph={{ rows: 1, width: 240 }}
              style={{ margin: '4px 0 0' }}
            />
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {hasSuggestions
                ? t('workOrders.kanban.optimizer.teaserSummary', {
                    count: teaser.suggestionCount,
                  })
                : t('workOrders.kanban.optimizer.teaserEmpty')}
            </Typography.Text>
          )}
        </div>
      </Flex>

      <Button
        type={hasSuggestions ? 'primary' : 'default'}
        onClick={onOpenSuggestions}
        style={
          hasSuggestions
            ? {
                background: WORK_ORDER_UI.primary,
                borderColor: WORK_ORDER_UI.primary,
              }
            : undefined
        }
      >
        <Flex align="center" gap={6}>
          {hasSuggestions
            ? t('workOrders.kanban.optimizer.teaserCta')
            : t('workOrders.kanban.optimizer.teaserCtaEmpty')}
          <ArrowDownOutlined />
        </Flex>
      </Button>
    </div>
  );
};
